/**
 * The one GraphQL boundary for activity events.
 *
 * Normal flushes go through GraphQLDataProvider.ExecuteGQL, exactly as
 * ResearchApiService.Execute does. The unload path cannot: ExecuteGQL's client
 * does not survive pagehide, so it posts the same operation with
 * `fetch(..., { keepalive: true })`, mirroring the headers the provider's own
 * client sends (authorization bearer + x-session-id). The 20-event batch cap
 * keeps the body far below the 64 KB keepalive budget.
 */
import { GraphQLDataProvider } from '@memberjunction/graphql-dataprovider';
import { ToUserActivityEventInput, type UserActivityEvent, type UserActivityEventInput } from './user-activity-event';
import type { UserActivityTransport } from './user-activity-queue';

/** Field names must match UserActivityEventInput / recordUserActivity in apps/MJAPI/src/resolvers/UserActivityResolver.ts. */
export const RECORD_USER_ACTIVITY_MUTATION = `
    mutation RecordUserActivity($events: [UserActivityEventInput!]!) {
        recordUserActivity(events: $events) { accepted written }
    }
`;

interface RecordUserActivityVariables {
    readonly events: ReadonlyArray<UserActivityEventInput>;
}

export class GraphQLUserActivityTransport implements UserActivityTransport {
    async Send(events: ReadonlyArray<UserActivityEvent>): Promise<void> {
        const variables: RecordUserActivityVariables = { events: events.map(ToUserActivityEventInput) };
        await GraphQLDataProvider.ExecuteGQL(RECORD_USER_ACTIVITY_MUTATION, variables, true);
    }

    SendOnUnload(events: ReadonlyArray<UserActivityEvent>): void {
        const provider = GraphQLDataProvider.Instance;
        const config = provider?.ConfigData;
        if (!config?.URL || !config.Token) return; // not configured yet: nothing to attribute the batch to
        const variables: RecordUserActivityVariables = { events: events.map(ToUserActivityEventInput) };
        const headers: Record<string, string> = {
            'content-type': 'application/json',
            authorization: `Bearer ${config.Token}`,
            'x-session-id': provider.sessionId,
        };
        void fetch(config.URL, {
            method: 'POST',
            keepalive: true,
            headers,
            body: JSON.stringify({ query: RECORD_USER_ACTIVITY_MUTATION, variables }),
        }).catch(() => undefined); // the page is going away; there is nobody to tell
    }
}
